"use client";

import { useCallback, useEffect, useState } from "react";
import Logo from "@/app/components/Logo";
import Button from "@/app/components/Button";
import CharacterCreate from "./CharacterCreate";
import CharacterReview from "./CharacterReview";
import { AccountView } from "@/lib/types";

const WORLD = "Baunheim";
const REFRESH_MS = 30000;

type RequestState = "waiting" | "working" | "review" | "rework";

interface RequestRow {
  id: string;
  mode: string;
  received_at: string;
  state: RequestState;
  name: string | null;
}

interface NpcRow {
  slug: string;
  name: string;
  rework: boolean;
}

const STATE_LABEL: Record<RequestState, string> = {
  waiting: "依頼中",
  working: "進行中",
  review: "確認待ち",
  rework: "手直しの依頼中",
};

type View = { kind: "list" } | { kind: "create" } | { kind: "review"; stage: "pending" | "master"; key: string; slug: string };

/** Baunheim の master の NPC を並べ、作成の依頼・確認・決定・手直しの依頼を受ける管理者用画面 */
export default function NpcAdmin({ onBack }: { onBack: () => void }) {
  const [view, setView] = useState<View>({ kind: "list" });
  const [account, setAccount] = useState<AccountView | null>(null);
  const [requests, setRequests] = useState<RequestRow[]>([]);
  const [npcs, setNpcs] = useState<NpcRow[]>([]);
  const [message, setMessage] = useState("読み込み中・・・");

  const load = useCallback(() => {
    fetch(`/api/admin/npc/list?world=${WORLD}`)
      .then(async (response) => {
        const body = await response.json();
        if (!response.ok) {
          setMessage(body.error ?? "一覧を読めません");
          return;
        }
        setRequests(body.requests as RequestRow[]);
        setNpcs(body.npcs as NpcRow[]);
        setMessage("");
      })
      .catch(() => setMessage("サーバーに繋がりません"));
  }, []);

  useEffect(() => {
    fetch("/api/auth/session")
      .then((response) => response.json())
      .then((body) => setAccount(body.account as AccountView | null))
      .catch(() => setMessage("サーバーに繋がりません"));
  }, []);

  // 一覧を開いている間は、依頼の状態の替わりを読み直す
  useEffect(() => {
    if (view.kind !== "list") return;
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [view.kind, load]);

  const toList = () => setView({ kind: "list" });

  if (view.kind === "create" && account) {
    return <CharacterCreate npc region="" account={account} requestId={null} onBack={toList} onDone={toList} />;
  }

  if (view.kind === "review" && account) {
    return (
      <CharacterReview
        npc={{ stage: view.stage, key: view.key }}
        region=""
        slug={view.slug}
        account={account}
        onBack={toList}
        onDone={toList}
      />
    );
  }

  return (
    <div className="panel panel-wide">
      <Logo />
      <div className="head">
        <span className="head-user">
          {WORLD}
          <strong>NPC</strong>
        </span>
        <Button onClick={onBack} inline>
          戻る
        </Button>
      </div>

      <div className="menu">
        <Button variant="primary" onClick={() => setView({ kind: "create" })} disabled={account === null}>
          新規作成
        </Button>
      </div>

      <section className="section">
        <h2 className="section-title">
          作成の依頼
          <span className="section-count">{requests.length} 件</span>
        </h2>
        <div className="slots">
          {requests.length === 0 ? <p className="section-note">依頼はありません。</p> : null}
          {requests.map((row) => (
            <div className="slot" key={row.id}>
              <div className="slot-body">
                <div className={row.name ? "slot-name" : "slot-name slot-name-empty"}>{row.name ?? "作成を依頼した NPC"}</div>
                <div className="slot-meta">
                  {row.id} ／ <span className="slot-state">{STATE_LABEL[row.state]}</span>
                </div>
              </div>
              <div className="slot-actions">
                {row.state === "review" ? (
                  <Button
                    variant="primary"
                    inline
                    onClick={() => setView({ kind: "review", stage: "pending", key: row.id, slug: row.name ?? row.id })}
                  >
                    確認
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <h2 className="section-title">
          master の NPC
          <span className="section-count">{npcs.length} 人</span>
        </h2>
        <div className="slots">
          {npcs.length === 0 ? <p className="section-note">NPC はいません。</p> : null}
          {npcs.map((row) => (
            <div className="slot" key={row.slug}>
              <div className="slot-body">
                <div className="slot-name">{row.name}</div>
                <div className="slot-meta">
                  {row.slug}
                  {row.rework ? (
                    <>
                      {" "}
                      ／ <span className="slot-state">手直しの依頼中</span>
                    </>
                  ) : null}
                </div>
              </div>
              <div className="slot-actions">
                <Button inline onClick={() => setView({ kind: "review", stage: "master", key: row.slug, slug: row.slug })}>
                  見る
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <p className="message message-info">{message}</p>
    </div>
  );
}
